// ============================================================
// FILE: SmsPreferences.js
// LOCATION: C:\gardenmarket\web\src\components\SmsPreferences.js
// ============================================================
// PURPOSE:
//   Lets a gardener opt in to SMS job alerts and send themselves
//   a test message.
//
// WHY THE WORDING MATTERS:
//   Australian Spam Act 2003 requires consent to be informed —
//   the user has to know what they are agreeing to receive. So
//   this states exactly which events trigger a text, and that
//   standard message rates apply. Please do not reduce it to a
//   bare "SMS notifications" toggle.
//
//   The toggle starts OFF for everyone, because consent defaults
//   to off in the database. That is the compliance position, not
//   an oversight.
//
// THE TEST BUTTON:
//   Worth more than it looks. Regional carrier delivery is not
//   guaranteed, and a gardener who never receives alerts will
//   assume the platform is dead rather than that their number is
//   wrong. One tap confirms the whole path works on their actual
//   handset.
// ============================================================

import { useState } from 'react';
import client from '../api/client';

export default function SmsPreferences({ initialOptIn = false, phone }) {
    const [optIn,    setOptIn]    = useState(!!initialOptIn);
    const [saving,   setSaving]   = useState(false);
    const [testing,  setTesting]  = useState(false);
    const [message,  setMessage]  = useState('');
    const [isError,  setIsError]  = useState(false);

    const setPref = async (next) => {
        setSaving(true);
        setMessage('');
        setIsError(false);
        try {
            const r = await client.put('/gardeners/sms-preferences', { sms_opt_in: next });
            setOptIn(!!r.data.sms_opt_in);
            setMessage(r.data.message || '');
            // Opted in but the number could not be parsed — surface it
            // rather than letting them believe alerts are working.
            setIsError(next && !r.data.textable);
        } catch (err) {
            setMessage(err.response?.data?.error || 'Could not save that. Please try again.');
            setIsError(true);
            setOptIn(!next);   // revert the toggle
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        setMessage('');
        setIsError(false);
        try {
            const r = await client.post('/gardeners/sms-test');
            setMessage(r.data.message || '');
            setIsError(!r.data.success);
        } catch (err) {
            setMessage(err.response?.data?.error || 'Could not send a test message.');
            setIsError(true);
        } finally {
            setTesting(false);
        }
    };

    return (
        <div style={styles.card}>
            <h3 style={styles.title}>Text message alerts</h3>

            <label style={styles.toggleRow}>
                <input
                    type="checkbox"
                    checked={optIn}
                    disabled={saving}
                    onChange={e => setPref(e.target.checked)}
                    style={styles.checkbox}
                />
                <span style={styles.toggleLabel}>
                    Text me when a job matches my area, and when a quote of mine is accepted
                </span>
            </label>

            {/* Informed consent: state exactly what is being agreed to. */}
            <p style={styles.fine}>
                Only these two alerts — we won't text you marketing. Standard message
                rates from your carrier may apply. You can turn this off any time here,
                or reply STOP to any message.
            </p>

            {phone && (
                <p style={styles.phoneLine}>
                    Texts go to <strong>{phone}</strong>. Change it in your account
                    details above if that's not right.
                </p>
            )}

            {optIn && (
                <button
                    type="button"
                    onClick={sendTest}
                    disabled={testing}
                    style={styles.testBtn}
                    className="gh-touch-target"
                >
                    {testing ? 'Sending...' : 'Send me a test message'}
                </button>
            )}

            {message && (
                <div style={isError ? styles.msgBad : styles.msgOk}>{message}</div>
            )}
        </div>
    );
}

const styles = {
    card:        { background:'white', borderRadius:'10px', padding:'20px',
                   boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginBottom:'16px' },
    title:       { fontSize:'1rem', fontWeight:'bold', color:'#2d6a4f',
                   margin:'0 0 12px' },
    toggleRow:   { display:'flex', alignItems:'flex-start', gap:'10px',
                   cursor:'pointer' },
    checkbox:    { marginTop:'3px', width:'18px', height:'18px', flexShrink:0 },
    toggleLabel: { fontSize:'0.9rem', color:'#333', lineHeight:1.5 },
    fine:        { fontSize:'0.78rem', color:'#888', lineHeight:1.6,
                   marginTop:'10px', marginBottom:'6px' },
    phoneLine:   { fontSize:'0.82rem', color:'#555', marginTop:'8px',
                   marginBottom:'12px' },
    testBtn:     { padding:'9px 16px', border:'1px solid #ddd', background:'white',
                   borderRadius:'6px', fontSize:'0.85rem', cursor:'pointer',
                   color:'#2d6a4f', fontWeight:600 },
    msgOk:       { marginTop:'12px', padding:'9px 12px', borderRadius:'6px',
                   backgroundColor:'#e8f5e9', color:'#2d6a4f', fontSize:'0.85rem' },
    msgBad:      { marginTop:'12px', padding:'9px 12px', borderRadius:'6px',
                   backgroundColor:'#fdecea', color:'#c0392b', fontSize:'0.85rem' },
};
